import { useHistory, useRouteMatch } from "react-router-dom";
import { AdviceDelivery } from "../api";
import { Application } from "../common/types";
import { useAppDispatch, useAppSelector } from "../store";
import { upsertAsync } from "../store/clientQuestionnaires";
import useApplications from "./useApplications";

interface Step {
  title: string;
  route: string;
  nextButtonLabel?: string;
}

const welcome = { title: "Welcome", route: "welcome", nextButtonLabel: "Start" };
const weAreNzFunds = { title: "We are NZ Funds", route: "we-are-nz-funds" };
const learnMore = { title: "Learn more about NZ Funds", route: "learn-more" };
const beginQuestions = { title: "Begin questions", route: "begin-questions" };
const ksLifeCycle = { title: "KiwiSaver: Life Cycle", route: "ks-life-cycle" };
const ksSelfSelect = { title: "KiwiSaver: Self Select", route: "ks-self-select" };
const ksAssetAllocationOptions = { title: "KiwiSaver: Asset allocation options", route: "ks-asset-allocation-options" };
const kiwiSaverCalculator = { title: "KiwiSaver Calculator", route: "kiwisaver-calculator" };
const contributionCalculator = { title: "Contribution Calculator", route: "contribution-calculator" };
const wealthBuilder = { title: "Introducing Wealth Builder", route: "wealth-builder" };
const wbLifeCycle = { title: "Wealth Builder: Life Cycle", route: "wb-life-cycle" };
const wbSelfSelect = { title: "Wealth Builder: Self Select", route: "wb-self-select" };
const wbAssetAllocationOptions = { title: "Wealth Builder: Asset allocation options", route: "wb-asset-allocation-options" };
const summary = { title: "Summary", route: "summary" };
const thanks = { title: "Thanks", route: "thanks" };

const generateNextStep = (steps: Step[], currentStep?: Step) => () => {
  if (currentStep) {
    const currentIndex = steps.findIndex((step) => step.route === currentStep.route);
    return steps[currentIndex + 1];
  } else {
    return steps[0];
  }
};

const getSteps = (applications: Application[]) => {
  const hasKiwiSaver = applications.includes(Application.KiwiSaver);
  const hasWealthBuilder = applications.includes(Application.WealthBuilder);

  const allProductsFlow: Step[] = [
    welcome,
    weAreNzFunds,
    learnMore,
    beginQuestions,
    ksLifeCycle,
    ksSelfSelect,
    ksAssetAllocationOptions,
    kiwiSaverCalculator,
    wealthBuilder,
    wbLifeCycle,
    wbSelfSelect,
    wbAssetAllocationOptions,
    contributionCalculator,
    summary,
    thanks,
  ];

  const kiwiSaverFlow: Step[] = [welcome, weAreNzFunds, learnMore, beginQuestions, ksLifeCycle, ksSelfSelect, ksAssetAllocationOptions, kiwiSaverCalculator, contributionCalculator, summary, thanks];

  if (hasKiwiSaver && !hasWealthBuilder) {
    return kiwiSaverFlow;
  } else {
    return allProductsFlow;
  }
};

const lastPathSegment = (path: string) => {
  const segments = path.split("/").filter((p) => p !== "");
  return segments[segments.length - 1].toLowerCase();
};

const deserializeStepHistory = (responses: Record<string, string>, currentRoute: string): string[] => {
  let stepHistoryString = responses["$$screensHistory$$"];
  if (!stepHistoryString) {
    if (currentRoute.includes("welcome")) stepHistoryString = currentRoute;
    else return [];
  }

  const stepHistory = stepHistoryString.split(", ");
  const currentIndex = stepHistory.lastIndexOf(currentRoute);
  if (currentIndex === -1) return stepHistory;
  return stepHistory.slice(0, currentIndex + 1);
};

const serializeStepHistory = (stepHistory: string[]) => ({ $$screensHistory$$: stepHistory.length === 0 ? "" : stepHistory.join(", ") });

export default function useSatSteps() {
  const { adviceDelivery, responses } = useAppSelector((state) => state.clientQuestionnaires);
  const history = useHistory();
  const match = useRouteMatch<{ clientId: string }>();
  const currentRoute = lastPathSegment(match.path);
  const clientId = match.params.clientId;
  const dispatch = useAppDispatch();
  const applications = useApplications();
  const steps = getSteps(applications);
  const currentStep = steps.find(({ route }) => route === currentRoute) || steps[0];
  const nextStep = generateNextStep(steps, currentStep);
  const nextStepRoute = () => nextStep()?.route;
  const stepHistory = deserializeStepHistory(responses ?? {}, currentRoute);

  const pushStepHistory = (newStepRoute: string): { $$screensHistory$$: string } => serializeStepHistory([...stepHistory, newStepRoute]);

  const goToNextStep = (overrideStepRoute?: string) => {
    const nextRoute = getNextRoute(overrideStepRoute);
    history.push(`/${adviceDelivery === AdviceDelivery.InPerson ? "adviser/sat" : "client"}/${clientId}/${nextRoute}`);
  };

  const getNextRoute = (overrideStepRoute?: string) => overrideStepRoute ?? nextStepRoute();

  const goToPrevStep = () => {
    const historyResponses = serializeStepHistory(stepHistory[stepHistory.length - 1] === currentRoute ? stepHistory.slice(0, -1) : stepHistory);
    const prevStepRoute = stepHistory[stepHistory.length - (stepHistory[stepHistory.length - 1] === currentRoute ? 2 : 1)];

    if (prevStepRoute && historyResponses) {
      dispatch(upsertAsync({ clientId, responses: historyResponses }));
      history.push(`/${adviceDelivery === AdviceDelivery.InPerson ? "adviser/sat" : "client"}/${clientId}/${prevStepRoute}`);
    }
  };

  const goToInitialStep = () => {
    const currentRoute = stepHistory[stepHistory.length - 1];
    history.replace(`/${adviceDelivery === AdviceDelivery.InPerson ? "adviser/sat" : "client"}/${clientId}/${!currentRoute ? steps[0]?.route : currentRoute}`);
  };

  return {
    steps,
    currentStep,
    getNextRoute,
    goToNextStep,
    goToPrevStep,
    goToInitialStep,
    pushStepHistory,
  };
}
